import { placeholder } from "./config";

/**
 * BIL-1..BIL-8: J360's own billing, as pure rules (the database and Stripe hold the facts).
 * Plans and modules come from the platform database keyed on plan / Stripe price ids (BIL-2);
 * prices here are the placeholders in config.ts until Alex and Ed set them.
 */

export const MODULES = ["crm", "scheduling", "invoicing", "receptionist", "websites"] as const;
export type Module = (typeof MODULES)[number];

export const MODULE_LABELS: Record<Module, string> = {
  crm: "Customers and jobs",
  scheduling: "Scheduling and online booking",
  invoicing: "Quotes and invoices",
  receptionist: "AI receptionist",
  websites: "Website",
};

/** Monthly list prices, cents (placeholders). */
export const PLAN_PRICE_CENTS: Record<string, number> = {
  starter: placeholder("plan.starter.price") * 100,
  team: placeholder("plan.team.price") * 100,
  team_receptionist: placeholder("plan.team_receptionist.price") * 100,
  receptionist_only: placeholder("plan.receptionist_only.price") * 100,
};
export const SEAT_PRICE_CENTS = placeholder("seat.price") * 100;
export const DUNNING_GRACE_DAYS = placeholder("dunning.grace_days");

export const SUBSCRIPTION_STATUSES = [
  "none",
  "active",
  "past_due",
  "canceled",
  "incomplete",
] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

/** What the next monthly charge is: the plan plus seats beyond the ones it includes. */
export function monthlyChargeCents(
  planId: string | null,
  seats: number,
  includedSeats: number,
): number {
  const base = planId ? (PLAN_PRICE_CENTS[planId] ?? 0) : 0;
  return base + Math.max(0, seats - includedSeats) * SEAT_PRICE_CENTS;
}

/**
 * BIL-4: can one more person be invited without buying a seat? `seats` is what is paid for
 * (never fewer than the plan includes); `used` counts active members plus pending invitations.
 */
export function seatCheck(
  seats: number | null,
  used: number,
): { ok: true } | { ok: false; needed: number } {
  if (seats === null) return { ok: true }; // no subscription: sales-assisted, not seat-limited
  return used + 1 <= seats ? { ok: true } : { ok: false, needed: used + 1 };
}

/**
 * BIL-7 dunning, as one function so webhooks, the daily job, and the admin simulator agree:
 *  - a failed payment makes the business past due (full service, banner, emails);
 *  - after the grace days it becomes read-only (suspended);
 *  - a successful payment restores it, unless staff paused it for another reason.
 */
export type DunningAction = "none" | "mark_past_due" | "suspend" | "restore";

export function dunningAction(s: {
  tenantStatus: string;
  subscriptionStatus: string;
  pastDueSince: string | null;
  now: number;
}): DunningAction {
  if (s.subscriptionStatus === "past_due") {
    if (s.tenantStatus === "active") return "mark_past_due";
    if (
      s.tenantStatus === "past_due" &&
      s.pastDueSince !== null &&
      s.now - Date.parse(s.pastDueSince) >= DUNNING_GRACE_DAYS * 24 * 60 * 60 * 1000
    )
      return "suspend";
    return "none";
  }
  if (s.subscriptionStatus === "active" && s.pastDueSince !== null)
    return s.tenantStatus === "past_due" || s.tenantStatus === "suspended" ? "restore" : "none";
  return "none";
}

/**
 * The plans shown at signup. Mirrors the `plans` seed (platform-db migration 0001); the
 * database stays the source of truth for what a subscription includes (BIL-2).
 */
export const PLAN_SUMMARIES: ReadonlyArray<{
  id: "starter" | "team" | "team_receptionist" | "receptionist_only";
  name: string;
  seats: number;
  modules: Module[];
  minutes: number;
}> = [
  { id: "starter", name: "Starter", seats: 1, modules: ["crm", "scheduling"], minutes: 0 },
  { id: "team", name: "Team", seats: 3, modules: ["crm", "scheduling", "invoicing"], minutes: 0 },
  {
    id: "team_receptionist",
    name: "Team + Receptionist",
    seats: 3,
    modules: ["crm", "scheduling", "invoicing", "receptionist"],
    minutes: 500,
  },
  {
    id: "receptionist_only",
    name: "Receptionist only",
    seats: 1,
    modules: ["receptionist"],
    minutes: 300,
  },
];

export const MONEY_BACK_DAYS = 30;

/** BIL-7 / NFR-7: after a cancellation takes effect the data is kept this long, then deleted. */
export const CANCEL_RETENTION_DAYS = 30;
/** NFR-7: the owner is emailed this many days before the deletion. */
export const DELETE_WARNING_DAYS = 7;

/**
 * BIL-7: when a cancellation takes effect. A paying business keeps service to the end of the
 * period it paid for; one we set up without a subscription (comp), or with no period on file,
 * ends right away.
 */
export function cancelAtFor(s: {
  comp: boolean;
  currentPeriodEnd: string | null;
  now: number;
}): string {
  const end = s.currentPeriodEnd ? Date.parse(s.currentPeriodEnd) : Number.NaN;
  if (s.comp || !Number.isFinite(end) || end <= s.now) return new Date(s.now).toISOString();
  return new Date(end).toISOString();
}

export function deleteAfterFor(canceledAt: number): string {
  return new Date(canceledAt + CANCEL_RETENTION_DAYS * 86_400_000).toISOString();
}
